import React, {
  PropsWithChildren,
  useState,
  useContext,
  useEffect,
} from 'react';
import { useFetch } from './FetchProvider';
import { useUserContext } from './UserContext';
import { getLearnPages } from '../lib/frontend/learn';
import { getSpreadjoyCount } from '../lib/frontend/spreadjoy';
import { LearnPagesData } from '@osmosis/shared/types/learn';
import { OnboardingVideoStep } from '@osmosis/shared/types/learn';
import { VideoStepType } from '@osmosis/shared/types/learn';

import { PROD_MEDIA_SERVER } from '@osmosis/constants';
import { FormattedMessage } from 'react-intl';
import useModal from '../hooks/useModal';
import { Tooltip } from '@osmosis/tailwind-ui/components/tooltip/Tooltip';
import { ZIndex } from '@osmosis/tailwind-ui/helpers/zIndex';

type StatusType = {
  videoRecsStep: {
    data: VideoStepType[];
    isComplete: boolean;
  };
  spreadjoyStep: {
    data: number;
    isComplete: boolean;
  };
};

export type OnboardingChecklistContextProps = {
  status?: StatusType;
  visible: boolean;
  update: () => Promise<void>;
  setVideoStepComplete: (
    learnNodeID: number,
    currentPercentage: number,
  ) => void;
};

const OnboardingChecklistContext =
  React.createContext<OnboardingChecklistContextProps>({
    status: undefined,
    visible: false,
    update: () => Promise.resolve(),
    setVideoStepComplete: () => Promise.resolve(),
  });

const headings = [
  <FormattedMessage
    key="second-step"
    id="onboarding-checklist-second-step"
    defaultMessage="Watch your first video"
  />,

  <FormattedMessage
    key="third-step"
    id="onboarding-checklist-third-step"
    defaultMessage="Watch your second video"
  />,
];

export const COMPLETED_PERCENTAGE = 80;

const formatTime = (timeInSeconds: number): string => {
  return new Date(timeInSeconds * 1000).toISOString().substr(14, 5);
};

const parseOnboardingVideoSteps = (
  productVideoRecs: LearnPagesData[],
): VideoStepType[] => {
  return productVideoRecs.map((item, i) => {
    const {
      learnNodeID,
      name,
      thumb,
      data: { dur: duration },
      watchStatus,
    } = item;

    return {
      learnNodeID,
      name,
      imageURL: `${PROD_MEDIA_SERVER}${thumb}`,
      duration: formatTime(Number(duration)),
      url: `learn/${name.replace(/ /g, '_')}`,
      watchStatus,
      heading: headings[i],
      isCompleted: watchStatus.percent > COMPLETED_PERCENTAGE,
    };
  });
};

const isVideoCompleted = (step: OnboardingVideoStep) => {
  return step.watchStatus.percent > COMPLETED_PERCENTAGE;
};

const LOCAL_STORAGE_KEY = 'onboarding:checklistComplete';

const hasCompletedOnboarding = () => {
  const onboarding = localStorage.getItem(LOCAL_STORAGE_KEY);
  return onboarding === 'true';
};

const setHasCompletedOnboarding = (value: boolean) => {
  localStorage.setItem(LOCAL_STORAGE_KEY, value.toString());
};

/**
 * Provider to wrap around the app and provide appropriate headers.
 */
export const OnboardingChecklistProvider = (
  props: PropsWithChildren,
): JSX.Element => {
  const { children } = props;

  const { post } = useFetch();
  const { user } = useUserContext();
  const [status, setStatus] = useState<StatusType>();
  const [visible, setVisible] = useState(false);
  const tooltip = useModal();

  const getRecommendations = async () => {
    // TODO: DEPRECATE THIS ENDPOINT AND JUST FETCH NECESSARY DATA
    if (user?.program) {
      const recommendations = await getLearnPages(post, user.program);
      return recommendations;
    }

    return [];
  };

  const getSpreadjoy = async () => {
    if (user) {
      const newCount = await getSpreadjoyCount(post);
      return newCount || 0;
    }

    return 0;
  };

  const setVideoStepComplete = (
    learnNodeID: number,
    currentPercentage: number,
  ) => {
    // If the onboarding checklist is visible
    if (status) {
      // If the current percentage is greater than the completed percentage
      if (currentPercentage > COMPLETED_PERCENTAGE) {
        const video = status?.videoRecsStep?.data.find(
          (v) => v.learnNodeID === learnNodeID,
        );

        // If the video isn't completed yet
        if (video && !video.isCompleted) {
          const newVideoRecsStepData = [...status.videoRecsStep.data];
          const index = newVideoRecsStepData.findIndex(
            (item) => item.learnNodeID === learnNodeID,
          );

          // Update video data
          if (index > -1) {
            newVideoRecsStepData[index].watchStatus.percent = 100;
            newVideoRecsStepData[index].isCompleted = true;
            setStatus({
              ...status,
              videoRecsStep: {
                ...status.videoRecsStep,
                data: newVideoRecsStepData,
                isComplete: newVideoRecsStepData.every(isVideoCompleted),
              },
            });

            // Show tooltip
            tooltip.show();
          }
        }
      }
    }
  };

  const update = async () => {
    const [learnPages, spreadjoyCount] = await Promise.all([
      getRecommendations(),
      getSpreadjoy(),
    ]);

    const productVideoRecs = parseOnboardingVideoSteps(learnPages);

    const newStatus = {
      videoRecsStep: {
        data: productVideoRecs,
        isComplete: productVideoRecs.every(isVideoCompleted),
      },
      spreadjoyStep: {
        data: spreadjoyCount,
        isComplete: spreadjoyCount > 0,
      },
    };

    setStatus(newStatus);

    // Set onboarding state
    if (
      newStatus.videoRecsStep.isComplete &&
      newStatus.spreadjoyStep.isComplete
    ) {
      setHasCompletedOnboarding(true);
    }
  };

  useEffect(() => {
    const shouldShow = !hasCompletedOnboarding();
    if (shouldShow) {
      update();
    }

    setVisible(shouldShow);
  }, []);

  useEffect(() => {
    if (tooltip.isVisible) {
      setTimeout(() => {
        tooltip.hide();
      }, 5000);
    }
  }, [tooltip.isVisible]);

  return (
    <OnboardingChecklistContext.Provider
      value={{
        status,
        visible,
        update,
        setVideoStepComplete,
      }}
    >
      <Tooltip
        content="Onboarding step complete!"
        isOpen={tooltip.isVisible}
        id="onboarding-tooltip"
        style={{ zIndex: ZIndex.PAGE_ACTION + 1 }}
      />

      {children}
    </OnboardingChecklistContext.Provider>
  );
};

/**
 * Hook to use the fetch context.
 */
export const useOnboardingChecklist = (): OnboardingChecklistContextProps =>
  useContext(OnboardingChecklistContext);
